import { headers, API_BASE_URL } from './apiClient'

const AGENT_CHAT_URL = `${API_BASE_URL}/v1/agent/chat`
const AGENT_INIT_URL = `${API_BASE_URL}/v1/agent/init`
const AGENT_REPLY_URL = `${API_BASE_URL}/v1/agent/reply`

/**
 * Classify the first message, create the appropriate resource, and stream routing + pk.
 *
 * @param {string} message
 * @param {object} callbacks
 * @param {function} [callbacks.onRouting]  - called with (stage1, stage2) when routing event arrives
 * @param {function} [callbacks.onPk]       - called with (type, pk) when pk event arrives — type is 'project' or 'chat'
 * @param {function} [callbacks.onQuestion] - called with a question object as each one completes (project flow only)
 * @param {function} [callbacks.onReady]    - called with the ready payload {domain, title, project_data} (project flow only)
 * @param {function} [callbacks.onChunk]    - called with a text string for each text chunk (other/decline reply)
 * @param {function} [callbacks.onDone]     - called when the done frame arrives (other/decline reply)
 * @param {function} [callbacks.onError]    - called with an error message string
 */
export async function sendAgentInit(message, { onRouting, onPk, onQuestion, onReady, onChunk, onDone, onError } = {}) {
  const response = await fetch(AGENT_INIT_URL, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ message }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || `Erreur HTTP ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let done = false

  while (!done) {
    const { value, done: doneReading } = await reader.read()
    done = doneReading
    const chunk = decoder.decode(value, { stream: true })
    const lines = chunk.split('\n\n').filter(l => l.trim().startsWith('data:'))

    for (const line of lines) {
      const raw = line.replace(/^data:\s*/, '').trim()
      if (!raw || raw === 'end') continue
      let parsed
      try {
        parsed = JSON.parse(raw)
      } catch {
        continue
      }
      if (parsed.event === 'routing') {
        onRouting?.(parsed.stage1, parsed.stage2)
      } else if (parsed.event === 'pk') {
        onPk?.(parsed.type, parsed.pk)
      } else if (parsed.event === 'question') {
        onQuestion?.(parsed.question)
      } else if (parsed.event === 'ready') {
        onReady?.(parsed)
      } else if (parsed.text !== undefined) {
        onChunk?.(parsed.text)
      } else if (parsed.done) {
        onDone?.()
      } else if (parsed.error) {
        onError?.(parsed.error)
      }
    }
  }
}

/**
 * Stream the agent reply for a message, parsing routing and text events.
 *
 * @param {number} conversationId
 * @param {string} message
 * @param {object} callbacks
 * @param {function} [callbacks.onRouting] - called with (stage1, stage2) when routing event arrives
 * @param {function} [callbacks.onChunk]   - called with a text string for each text chunk
 * @param {function} [callbacks.onDone]    - called when the done frame arrives
 * @param {function} [callbacks.onError]   - called with an error message string on error frames
 */
export async function sendAgentMessageStream(conversationId, message, { onRouting, onChunk, onDone, onError } = {}) {
  const response = await fetch(AGENT_CHAT_URL, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ conversation_id: conversationId, message }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || `Erreur HTTP ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let done = false

  while (!done) {
    const { value, done: doneReading } = await reader.read()
    done = doneReading
    const chunk = decoder.decode(value, { stream: true })
    const lines = chunk.split('\n\n').filter(l => l.trim().startsWith('data:'))

    for (const line of lines) {
      const raw = line.replace(/^data:\s*/, '').trim()
      if (!raw || raw === 'end') continue
      let parsed
      try {
        parsed = JSON.parse(raw)
      } catch {
        continue
      }
      if (parsed.event === 'routing') {
        onRouting?.(parsed.stage1, parsed.stage2)
      } else if (parsed.text !== undefined) {
        onChunk?.(parsed.text)
      } else if (parsed.done) {
        onDone?.()
      } else if (parsed.error) {
        onError?.(parsed.error)
      }
    }
  }
}

/**
 * Stream the agent reply for the last unanswered user message in an existing conversation.
 * Does not create a new user message — safe to call right after agentInit for the chat case.
 *
 * @param {number} conversationId
 * @param {object} callbacks
 * @param {function} [callbacks.onRouting] - called with (stage1, stage2)
 * @param {function} [callbacks.onChunk]   - called with a text string for each chunk
 * @param {function} [callbacks.onDone]    - called when the done frame arrives
 * @param {function} [callbacks.onTitle]   - called with the generated title string (first reply only)
 * @param {function} [callbacks.onError]   - called with an error message string
 */
export async function sendAgentReply(conversationId, { stage1, stage2, onRouting, onChunk, onDone, onTitle, onError } = {}) {
  const body = { conversation_id: conversationId }
  if (stage1) body.stage1 = stage1
  if (stage2) body.stage2 = stage2

  const response = await fetch(AGENT_REPLY_URL, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || `Erreur HTTP ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let done = false

  while (!done) {
    const { value, done: doneReading } = await reader.read()
    done = doneReading
    const chunk = decoder.decode(value, { stream: true })
    const lines = chunk.split('\n\n').filter(l => l.trim().startsWith('data:'))

    for (const line of lines) {
      const raw = line.replace(/^data:\s*/, '').trim()
      if (!raw || raw === 'end') continue
      let parsed
      try {
        parsed = JSON.parse(raw)
      } catch {
        continue
      }
      if (parsed.event === 'routing') {
        onRouting?.(parsed.stage1, parsed.stage2)
      } else if (parsed.event === 'title') {
        onTitle?.(parsed.title)
      } else if (parsed.text !== undefined) {
        onChunk?.(parsed.text)
      } else if (parsed.done) {
        onDone?.()
      } else if (parsed.error) {
        onError?.(parsed.error)
      }
    }
  }
}
